import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { TrendingUp, AlertCircle, CheckCircle, Ship, ExternalLink, FileText, Download } from 'lucide-react';
import { DataTable } from '../../components/ui/data-table';
import PackageStatusBadge from '../../components/PackageStatusBadge';
import {
  fmt,
  fmtCurrency,
  KpiCard,
  PaymentProgressBar,
  getInvoiceStatus,
  InvoiceStatusBadge,
} from './shared';

// ─────────────────────────────────────────────────────────────────────────────
// DebtAgingBar
// ─────────────────────────────────────────────────────────────────────────────
const DebtAgingBar = ({ invoices, t }) => {
  const now = Date.now();
  const buckets = { lt30: 0, b3060: 0, b6090: 0, gt90: 0 };
  invoices.forEach((inv) => {
    if (inv.is_settled || !inv.amount_remaining) return;
    const age = inv.created_at ? Math.floor((now - new Date(inv.created_at).getTime()) / 86400000) : 0;
    if (age < 30) buckets.lt30 += inv.amount_remaining;
    else if (age < 60) buckets.b3060 += inv.amount_remaining;
    else if (age < 90) buckets.b6090 += inv.amount_remaining;
    else buckets.gt90 += inv.amount_remaining;
  });
  const total = buckets.lt30 + buckets.b3060 + buckets.b6090 + buckets.gt90;
  if (total === 0) return null;

  const segments = [
    { key: 'lt30', label: t('lessThan30d'), value: buckets.lt30, color: 'bg-green-400' },
    { key: 'b3060', label: t('between30and60d'), value: buckets.b3060, color: 'bg-amber-400' },
    { key: 'b6090', label: t('between60and90d'), value: buckets.b6090, color: 'bg-orange-500' },
    { key: 'gt90', label: t('moreThan90d'), value: buckets.gt90, color: 'bg-red-500' },
  ];

  return (
    <Card className="border-border">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">{t('debtAgingTitle')}</h3>
          <span className="text-xs text-muted-foreground">{fmt(total)}</span>
        </div>
        <div className="flex h-3 rounded-full overflow-hidden gap-0.5">
          {segments.map((s) => s.value > 0 && (
            <div
              key={s.key}
              className={`${s.color} transition-all`}
              style={{ width: `${(s.value / total) * 100}%` }}
              title={`${s.label}: ${fmt(s.value)}`}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-4 mt-2">
          {segments.map((s) => (
            <div key={s.key} className="flex items-center gap-1.5 text-xs">
              <span className={`w-2.5 h-2.5 rounded-sm ${s.color}`} />
              <span className="text-muted-foreground">{s.label}</span>
              <span className="font-medium text-foreground">{fmt(s.value)}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FinanceInvoicesPage
// ─────────────────────────────────────────────────────────────────────────────
const FinanceInvoicesPage = () => {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || 'all';
  const initialSearch = searchParams.get('search') || '';
  const locale = language === 'en' ? 'en-GB' : 'fr-FR';
  const [allInvoices, setAllInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [shipmentFilter, setShipmentFilter] = useState('all');
  const [globalSearch, setGlobalSearch] = useState(initialSearch);
  const [selected, setSelected] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [addingPayment, setAddingPayment] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/invoices');
      setAllInvoices(r.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const now = new Date();
  const thisMonth = now.getMonth();
  const thisYear = now.getFullYear();

  const issuedThisMonth = allInvoices.filter((inv) => {
    if (!inv.created_at) return false;
    const d = new Date(inv.created_at);
    return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
  });
  const kpiIssued = issuedThisMonth.reduce((s, inv) => s + (inv.total_amount || 0), 0);
  const kpiCollected = allInvoices.reduce((s, inv) => s + (inv.amount_paid || 0), 0);
  const kpiPending = allInvoices.filter((inv) => !inv.is_settled).reduce((s, inv) => s + (inv.amount_remaining || 0), 0);
  const overdueInvoices = allInvoices.filter((inv) => getInvoiceStatus(inv) === 'overdue');
  const kpiOverdue = overdueInvoices.reduce((s, inv) => s + (inv.amount_remaining || 0), 0);

  const delays = allInvoices
    .filter((inv) => inv.payments?.length > 0 && inv.created_at)
    .map((inv) => {
      const created = new Date(inv.created_at).getTime();
      const firstPay = new Date(inv.payments[0].paid_at).getTime();
      return Math.max(0, Math.floor((firstPay - created) / 86400000));
    });
  const avgDelay = delays.length > 0 ? Math.round(delays.reduce((a, b) => a + b, 0) / delays.length) : 0;

  const invoiceGlobalFilter = useCallback((row, _columnId, filterValue) => {
    if (!filterValue) return true;
    const q = filterValue.toLowerCase();
    const inv = row.original;
    return [
      inv.invoice_number,
      inv.client_name,
      inv.client_email,
      inv.client_phone,
      inv.package_oce_code,
      inv.shipment_name,
      inv.shipment_route,
      inv.currency,
      String(inv.total_amount || ''),
    ].some((v) => v && v.toLowerCase().includes(q));
  }, []);

  // Unique shipment names for filter
  const shipmentNames = [...new Set(allInvoices.map(i => i.shipment_name).filter(Boolean))].sort();

  const filtered = allInvoices.filter((inv) => {
    // Shipment filter
    if (shipmentFilter !== 'all' && inv.shipment_name !== shipmentFilter) return false;
    // Status filter
    if (statusFilter === 'all') return true;
    const st = getInvoiceStatus(inv);
    if (statusFilter === 'pending') return st === 'pending';
    if (statusFilter === 'settled') return st === 'paid';
    if (statusFilter === 'overdue') return st === 'overdue';
    return true;
  });

  const filterTabs = [
    { key: 'all', label: t('allInvoices'), count: allInvoices.length },
    { key: 'pending', label: t('pendingFilter'), count: allInvoices.filter((i) => getInvoiceStatus(i) === 'pending').length },
    { key: 'settled', label: t('paidFilter'), count: allInvoices.filter((i) => i.is_settled).length },
    { key: 'overdue', label: t('overdueFilter'), count: overdueInvoices.length },
  ];

  const addPayment = async () => {
    if (!selected || !paymentAmount) return;
    setAddingPayment(true);
    try {
      const r = await api.post(
        `/api/invoices/${selected.invoice_id}/payments`,
        { amount: parseFloat(paymentAmount), currency: selected.currency, note: paymentNote }
      );
      setSelected(r.data);
      setPaymentAmount('');
      setPaymentNote('');
      fetchInvoices();
      setMsg(t('paymentRecorded'));
    } catch { setMsg(t('paymentError')); }
    setAddingPayment(false);
  };

  const sendInvoice = async () => {
    if (!selected) return;
    setSending(true);
    try {
      const r = await api.post(`/api/invoices/${selected.invoice_id}/send`);
      setMsg(r.data.sent_via ? `${t('sentVia')} ${r.data.sent_via}` : t('sendFailed'));
    } catch { setMsg(t('sendError')); }
    setSending(false);
  };

  const downloadPdf = async () => {
    if (!selected) return;
    try {
      const response = await api.get(
        `/api/invoices/${selected.invoice_id}/pdf`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selected.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setMsg(t('pdfDownloadError'));
    }
  };

  return (
    <div className="space-y-5">
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <KpiCard icon={FileText} label={t('issuedThisMonth')} value={fmt(kpiIssued)} sub={`${issuedThisMonth.length} ${t('invoiceCount')}`} color="blue" />
          <KpiCard icon={CheckCircle} label={t('collectedAmount')} value={fmt(kpiCollected)} color="green" />
          <KpiCard icon={AlertCircle} label={t('pendingAmount')} value={fmt(kpiPending)} color="amber" />
          <KpiCard icon={AlertCircle} label={t('overdueAmount')} value={fmt(kpiOverdue)} sub={`${overdueInvoices.length} ${t('invoiceCount')}`} color="red" />
          <KpiCard icon={TrendingUp} label={t('avgPaymentDelay')} value={`${avgDelay} ${t('daysUnit')}`} color="slate" />
        </div>
      )}

      {!loading && <DebtAgingBar invoices={allInvoices} t={t} />}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {filterTabs.map((f) => (
            <Button
              key={f.key}
              variant={statusFilter === f.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(f.key)}
            >
              {f.label}
              <span className="ml-1.5 text-[10px] opacity-70">({f.count})</span>
            </Button>
          ))}
        </div>
        {shipmentNames.length > 0 && (
          <select
            value={shipmentFilter}
            onChange={(e) => setShipmentFilter(e.target.value)}
            className="border border-border rounded-md px-3 py-1.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="all">{language === 'en' ? 'All departures' : 'Tous les départs'}</option>
            {shipmentNames.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        )}
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-8">{t('loading')}</div>
      ) : (
        <DataTable
          data={filtered}
          searchPlaceholder={t('searchInvoiceClient')}
          globalFilter={globalSearch}
          onGlobalFilterChange={setGlobalSearch}
          globalFilterFn={invoiceGlobalFilter}
          emptyMessage={t('noInvoiceFound')}
          columns={[
            {
              accessorKey: 'invoice_number',
              header: t('invoiceNumber'),
              cell: ({ row }) => (
                <span className="font-mono text-xs font-medium text-foreground">{row.original.invoice_number}</span>
              ),
            },
            {
              accessorKey: 'client_name',
              header: t('client'),
              cell: ({ row }) => (
                <div>
                  <div className="font-medium text-foreground text-sm">{row.original.client_name}</div>
                  <div className="text-xs text-muted-foreground">{row.original.client_type === 'sender' ? t('sender') : t('recipient')}</div>
                </div>
              ),
            },
            {
              accessorKey: 'package_oce_code',
              header: t('oceCode'),
              cell: ({ row }) => (
                <span className="font-mono text-xs text-foreground">{row.original.package_oce_code || '—'}</span>
              ),
            },
            {
              accessorFn: (row) => row.package_status || '',
              id: 'package_status',
              header: t('packageStatus'),
              cell: ({ row }) => {
                const s = row.original.package_status;
                if (!s) return <span className="text-muted-foreground text-xs">—</span>;
                return <PackageStatusBadge status={s} size="sm" />;
              },
            },
            {
              accessorFn: (row) => row.created_at ? new Date(row.created_at).getTime() : 0,
              id: 'issued_date',
              header: t('issuedOn'),
              cell: ({ row }) => (
                <span className="text-xs text-muted-foreground">
                  {row.original.created_at ? new Date(row.original.created_at).toLocaleDateString(locale) : '—'}
                </span>
              ),
            },
            {
              accessorFn: (row) => row.shipment_name || '',
              id: 'shipment',
              header: t('shipment'),
              cell: ({ row }) => {
                const s = row.original;
                if (!s.shipment_name) return <span className="text-muted-foreground text-xs">—</span>;
                return (
                  <div className="text-xs">
                    <div className="font-medium text-foreground">{s.shipment_name}</div>
                    <div className="text-muted-foreground">{s.shipment_route}</div>
                  </div>
                );
              },
            },
            {
              accessorFn: (row) => row.total_amount > 0 ? (row.amount_paid / row.total_amount) : 0,
              id: 'payment_progress',
              header: t('paymentProgress'),
              cell: ({ row }) => (
                <PaymentProgressBar
                  paid={row.original.amount_paid}
                  total={row.original.total_amount}
                  currency={row.original.currency}
                />
              ),
            },
            {
              accessorFn: (row) => getInvoiceStatus(row) === 'paid' ? 2 : getInvoiceStatus(row) === 'overdue' ? 0 : 1,
              id: 'status',
              header: t('status'),
              cell: ({ row }) => <InvoiceStatusBadge status={getInvoiceStatus(row.original)} t={t} />,
              enableSorting: true,
            },
            {
              accessorKey: 'total_amount',
              header: t('total'),
              cell: ({ row }) => (
                <span className="font-semibold text-foreground">{fmtCurrency(row.original.total_amount, row.original.currency)}</span>
              ),
              meta: { className: 'text-right', cellClassName: 'text-right' },
            },
            {
              id: 'actions',
              header: '',
              cell: ({ row }) => (
                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setMsg(''); }}>
                  <ExternalLink size={14} className="mr-1" />
                  {t('detail')}
                </Button>
              ),
              meta: { cellClassName: 'text-right' },
              enableSorting: false,
            },
          ]}
        />
      )}

      {selected && (
        <Dialog open={!!selected} onOpenChange={() => { setSelected(null); setMsg(''); }}>
          <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-3 flex-wrap">
                <DialogTitle className="text-xl">{t('invoice')} {selected.invoice_number}</DialogTitle>
                <InvoiceStatusBadge status={getInvoiceStatus(selected)} t={t} />
              </div>
            </DialogHeader>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-5">
                <div className="bg-muted rounded-lg p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-xs text-muted-foreground block">{t('billedTo')}</span>
                      <span className="font-semibold text-foreground">{selected.client_name}</span>
                      <span className="text-muted-foreground text-xs block">
                        {selected.client_type === 'sender' ? t('sender') : t('recipient')}
                      </span>
                      {selected.client_email && (
                        <span className="text-xs text-muted-foreground block">{selected.client_email}</span>
                      )}
                      {selected.client_phone && (
                        <span className="text-xs text-muted-foreground block">{selected.client_phone}</span>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-muted-foreground block">{t('issuedOn')}</span>
                      <span className="font-medium text-foreground">
                        {selected.created_at ? new Date(selected.created_at).toLocaleDateString(locale) : '—'}
                      </span>
                      {selected.sent_at && (
                        <>
                          <span className="text-xs text-muted-foreground block mt-2">{t('sentVia')}</span>
                          <span className="text-xs text-foreground">{selected.sent_via} — {new Date(selected.sent_at).toLocaleDateString(locale)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="border border-border rounded-lg overflow-x-auto">
                  <table className="analytics-table w-full min-w-[400px]">
                    <thead>
                      <tr>
                        <th>{t('designation')}</th>
                        <th style={{ textAlign: 'right' }}>{t('quantity')}</th>
                        <th style={{ textAlign: 'right' }}>{t('unitPriceHT')}</th>
                        <th style={{ textAlign: 'right' }}>{t('totalHT')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <div>
                            <span className="font-medium text-foreground">
                              {selected.package_oce_code || selected.package_id?.slice(-8)}
                            </span>
                            {selected.package_weight && (
                              <span className="text-xs text-muted-foreground ml-2">({selected.package_weight} kg)</span>
                            )}
                          </div>
                          {selected.shipment_route && (
                            <span className="text-xs text-muted-foreground">{selected.shipment_route}</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>1</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {fmtCurrency(selected.total_amount, selected.currency)}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} className="font-semibold">
                          {fmtCurrency(selected.total_amount, selected.currency)}
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-border">
                        <td colSpan={3} className="font-semibold text-foreground text-right">{t('subtotal')}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} className="font-bold text-foreground">
                          {fmtCurrency(selected.total_amount, selected.currency)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="border border-border rounded-md p-3 bg-muted">
                    <div className="text-xs text-muted-foreground mb-1">{t('total')}</div>
                    <div className="font-bold text-foreground">{fmtCurrency(selected.total_amount, selected.currency)}</div>
                  </div>
                  <div className="border border-green-200 rounded-md p-3 bg-green-50">
                    <div className="text-xs text-muted-foreground mb-1">{t('paidAmount')}</div>
                    <div className="font-bold text-green-700">{fmtCurrency(selected.amount_paid, selected.currency)}</div>
                  </div>
                  <div className={`border rounded-md p-3 ${selected.is_settled ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                    <div className="text-xs text-muted-foreground mb-1">{t('remainingDue')}</div>
                    <div className={`font-bold ${selected.is_settled ? 'text-green-700' : 'text-red-700'}`}>
                      {fmtCurrency(selected.amount_remaining, selected.currency)}
                    </div>
                  </div>
                </div>

                {!selected.is_settled && (
                  <div className="border border-border rounded-lg p-4 space-y-3">
                    <h4 className="font-semibold text-sm text-foreground">{t('addPayment')}</h4>
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">{t('amount')}</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          className="w-36"
                        />
                      </div>
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs text-muted-foreground">{t('noteOptional')}</Label>
                        <Input
                          placeholder={t('noteOptional')}
                          value={paymentNote}
                          onChange={(e) => setPaymentNote(e.target.value)}
                        />
                      </div>
                      <Button onClick={addPayment} disabled={addingPayment || !paymentAmount} size="sm">
                        {addingPayment ? t('recording') : t('recordPayment')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{t('paymentProgress')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <PaymentProgressBar paid={selected.amount_paid} total={selected.total_amount} currency={selected.currency} />
                      <div className="text-center text-sm font-semibold text-foreground">
                        {fmtCurrency(selected.amount_paid, selected.currency)} / {fmtCurrency(selected.total_amount, selected.currency)}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{t('paymentHistory')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selected.payments?.length > 0 ? (
                      <div className="space-y-0">
                        {selected.payments.map((p, i) => (
                          <div key={i} className="flex items-start gap-3 pb-3 last:pb-0">
                            <div className="mt-1 w-2 h-2 rounded-full bg-green-500 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-baseline">
                                <span className="text-xs font-medium text-foreground">
                                  {fmtCurrency(p.amount, p.currency)}
                                </span>
                                <span className="text-[10px] text-muted-foreground">
                                  {p.paid_at ? new Date(p.paid_at).toLocaleDateString(locale) : ''}
                                </span>
                              </div>
                              {p.note && <p className="text-[11px] text-muted-foreground mt-0.5">{p.note}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">{t('noPaymentsYet')}</p>
                    )}
                  </CardContent>
                </Card>

                {selected.shipment_name && (
                  <Card className="border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-1.5">
                        <Ship size={14} />
                        {t('linkedShipment')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1 text-sm">
                        <div className="font-medium text-foreground">{selected.shipment_name}</div>
                        <div className="text-xs text-muted-foreground">{selected.shipment_route}</div>
                        {selected.shipment_departure_date && (
                          <div className="text-xs text-muted-foreground">
                            {new Date(selected.shipment_departure_date).toLocaleDateString(locale)}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="space-y-2">
                  <Button variant="outline" size="sm" className="w-full justify-start" onClick={downloadPdf}>
                    <Download size={14} className="mr-2" />
                    {t('downloadPdf')}
                  </Button>
                  <Button size="sm" className="w-full justify-start" onClick={sendInvoice} disabled={sending}>
                    <ExternalLink size={14} className="mr-2" />
                    {sending ? t('sending') : t('sendToClient')}
                  </Button>
                </div>

                {msg && <p className="text-xs text-green-600 bg-green-50 p-2 rounded">{msg}</p>}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default FinanceInvoicesPage;
